<template>
  <div>
    <Teleport to="#modals">
      <Transition name="modal-fade" appear>
        <component
          v-if="modal.show.value"
          :is="modal.component.value"
          v-bind="modal.props.value"
          @close="handleClose"
        />
      </Transition>
    </Teleport>

    <button
      class="svg-button svg-button--search-modal search-modal-button"
      title="Open Search"
      type="button"
      @click="openConfirm"
      @mouseenter="preloadModal"
      @focus="preloadModal"
    >
      <svg
        height="24"
        width="24"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      >
        <path
          d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
        />
      </svg>
    </button>
  </div>
</template>

<script setup>
import { markRaw, onMounted } from 'vue';
import { useModal } from '../composables/useModal.ts';

const modal = useModal();

const props = defineProps({
  rootId: {
    type: Number,
    required: true
  },
  filters: {
    type: Array,
    default: () => []
  }
});

/**
 * Helpers for reading URL state
 */
const getSearchParams = () => {
  try {
    const url = new URL(window.location.href);
    return {
      open: url.searchParams.get('searchOpen') === '1',
      term: url.searchParams.get('search') || ''
    };
  } catch {
    return { open: false, term: '' };
  }
};

/**
 * Async loader for the modal component
 */
let preloadPromise = null;

const loadModal = () => {
  if (!preloadPromise) {
    preloadPromise = import(
      /* webpackChunkName: 'SearchModal', webpackPrefetch: true */
      '../components/SearchModal.vue'
    );
  }
  return preloadPromise;
};

const preloadModal = () => {
  // fire and forget, just to warm the chunk
  void loadModal();
};

/**
 * Open the modal (normal click)
 */
const openConfirm = async () => {
  const { term } = getSearchParams();
  const module = await loadModal();
  const SearchModal = module.default || module;

  modal.component.value = markRaw(SearchModal);
  modal.showModal({
    rootId: props.rootId,
    initialSearchTerm: term,
    filters: props.filters
  });
};

/**
 * Close handler from the modal
 * (SearchModal itself already clears query params & state)
 */
const handleClose = () => {
  modal.hideModal();
};

/**
 * Auto-open on page load when coming back via Back button
 * and URL has searchOpen=1&search=...
 */
onMounted(async () => {
  const { open, term } = getSearchParams();

  if (!open || !term) {
    return;
  }

  const module = await loadModal();
  const SearchModal = module.default || module;

  modal.component.value = markRaw(SearchModal);
  modal.showModal({
    rootId: props.rootId,
    initialSearchTerm: term,
    filters: props.filters
  });
});
</script>